/**
 * The chat surface — the app's centre, in the shape Claude uses: an empty state
 * that is really just the composer under a greeting, and a transcript once a
 * conversation has started.
 *
 * The turn is **real** (item AE). `POST /ask` goes to the local agent, the
 * NDJSON stream is read as it arrives, and the answer, the model's reasoning and
 * every tool the agent ran land in the transcript. Approvals stop the turn until
 * they are answered, and the context meter sits beside the composer.
 *
 * The thread side (item AF) is the transcript of whichever conversation is
 * open. The list of them is the shell's now — the sidebar's Chats section and
 * the Chats page — so this pane only says when to reload it.
 *
 * The Revit Database panel is rendered from here but *into the sidebar*, through
 * a portal: it reads this pane's document context and inserts into this pane's
 * composer, so its state stays here while its place on screen is the shell's.
 *
 * The composer is **disabled with a reason** whenever the agent is not ready,
 * never enabled-and-failing — item K's rule.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import type { ChatSessions } from "../lib/chatSessions";
import { useChatTurn, type ChatMessage } from "../hooks/useChatTurn";
import type { HostsView } from "../hooks/useHosts";
import type { PluginsController } from "../hooks/usePlugins";
import { useAttachments } from "../hooks/useAttachments";
import { useModels } from "../hooks/useModels";
import { useLocale } from "../i18n/LocaleProvider";
import type { HostKind } from "../lib/hostStatus";
import { type AgentTarget } from "../lib/agentClient";
import { API_BASE_URL } from "../lib/config";
import { resolveContextUsage } from "../lib/chat";
import { detectCsvLinks } from "../lib/csv";
import { findFreshStarterPromptItem, renderStarterPrompt, starterPromptToEditorContent } from "../lib/starterPrompts";
import { useDocumentContext } from "../hooks/useDocumentContext";
import { DocumentContextProvider } from "../contexts/DocumentContextProvider";
import type { DocumentContextSuggestionConfig } from "../extensions/DocumentContextSuggestion";
import { MentionEditor, type MentionEditorHandle } from "../components/chat/MentionEditor";
import { UserMessageText } from "../components/chat/UserMessageText";
import { CopyButton } from "../components/chat/CopyButton";
import { RevitIndexPanel } from "../components/chat/RevitIndexPanel";
import { ContinueBar, WORKFLOW_SECTIONS, defaultSection, openWorkflow, rememberThreadWorkflow, useContinueRun, useThreadWorkflow, type WorkflowSectionId } from "../workflows";
import { useWorkflowList, useWorkflowSections } from "../workflows/useWorkflowSections";
import { workflowAbout, workflowText } from "../lib/workflowList";
import { closePanel, composeSections, openPanel as openPanelFor } from "../lib/workflowSections";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { SIDEBAR_DRAWER_QUERY } from "../lib/sidebarView";
import type { DocumentContextMentionSource } from "../lib/documentContextMention";
import { supportsReasoningEffort, type AgentMode } from "../lib/models";
import { useReasoningEffort } from "../hooks/useReasoningEffort";
import { ACCEPT_ATTR, sentAttachmentsFromUrls, storedMessageText } from "../lib/attachments";
import { getThreadMessages, shouldLoadHistory, statusOf, type ThreadMessage } from "../lib/threads";
import { AgentSteps } from "../components/chat/AgentSteps";
import { CadChangeLinks } from "../components/chat/CadChangeLinks";
import { cadReviewFromMessages } from "../lib/cadChanges";
import { MessageAttachments } from "../components/chat/MessageAttachments";
import { MessageError } from "../components/chat/MessageError";
import { CsvPreview } from "../components/chat/CsvPreview";
import { SaveSkillCard } from "../components/chat/SaveSkillCard";
import { ReasoningSection } from "../components/chat/ReasoningSection";
import { ActivePlanPanel } from "../components/chat/ActivePlanPanel";
import { ComposerError } from "../components/chat/ComposerError";
import { AgentModeSelector, ModelSelector } from "../components/chat/ComposerControls";
import { ContextMeter } from "../components/chat/ContextMeter";
import { InterruptPanel } from "../components/chat/InterruptPanel";
import { Markdown } from "../components/chat/Markdown";
import { StarterPromptPanel, type StarterSelection } from "../components/chat/StarterPromptPanel";
import { PluginSetupCard } from "../components/chat/PluginSetupCard";
import { PluginUpdateCards } from "../components/chat/PluginUpdateCards";
import { artifactTarget, createSkillsClient } from "../lib/skills";
import { LoadingBlock, Spinner } from "../components/settings/Spinner";
import { InfoIcon } from "../components/skills/icons";


/**
 * `sender` is the API's word; the transcript speaks in roles.
 *
 * `images` is the backend's legacy column and the only place a past message's
 * attachments survive — URLs, with no filename or kind beside them — so both are
 * inferred from the URL (`sentAttachmentsFromUrls`). Without this a reloaded
 * thread showed the text of a message and no sign it had carried a file.
 */
function historyToMessages(messages: ThreadMessage[]): ChatMessage[] {
  return messages.map((m) => {
    const attachments = sentAttachmentsFromUrls(m.images);
    return {
      id: `h-${m.id}`,
      role: m.sender === "user" ? ("user" as const) : ("assistant" as const),
      text: storedMessageText(m.content, attachments),
      ...(attachments.length > 0 ? { attachments } : {}),
    };
  });
}

type HistoryState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded" };

export default function ThreadsPane({
  agent,
  projectId,
  orgId,
  userId,
  token,
  activeThreadId,
  onSelectThread,
  targetSessionId,
  targetSessions,
  revitVersion,
  onBusyChange,
  mayCreateSkill = false,
  visible = true,
  sessions,
  newChat = 0,
  reloadThreads,
  databaseSlot,
  databaseOpen,
  onCloseDatabase,
  workflowId = null,
  onOpenWorkflows,
  hosts,
  plugins,
  onOpenPlugins,
}: {
  hosts: HostsView;
  plugins: PluginsController;
  onOpenPlugins: () => void;
  /**
   * The workflow the user entered from its card on the Workflows page, for the
   * conversation it is about to start. A saved conversation's own workflow is
   * read from its work settings instead (`useThreadWorkflow`).
   */
  workflowId?: string | null;
  /** Back to the Workflows page, from a workflow's view. */
  onOpenWorkflows?: () => void;
  /**
   * Refetch the project's conversation list, which the shell holds: a turn that
   * creates a thread, or settles one, changes what the sidebar should show.
   */
  reloadThreads: () => void;
  /** The sidebar's slot for the Revit Database panel, once it exists. */
  databaseSlot: HTMLElement | null;
  /** The panel is open — the shell's decision (`resolveSidebarView`). */
  databaseOpen: boolean;
  onCloseDatabase: () => void;
  /** Bumped by the shell's "New" — a fresh chat even when none was open (#193). */
  newChat?: number;
  agent: AgentTarget;
  /** Every conversation's live state, held by the shell (#193). */
  sessions: ChatSessions;
  /**
   * Whether the chat is the pane on screen. The shell keeps it mounted while another
   * pane is open, so a running turn is not aborted by navigating away (#189).
   */
  visible?: boolean;
  /** `skill:create` — what makes the source-code modal offer Save Skill. */
  mayCreateSkill?: boolean;
  /**
   * The project this conversation is bound to. `/ask` carries `project_id`, so
   * showing which one is selected is the difference between "asking about a
   * model" and "asking into the void".
   */
  projectId: string | null;
  /** `/ask` requires it — the agent 400s without one. */
  orgId: string | null;
  userId: string | null;
  token: string | null;
  /** The open conversation, or null for a new one. */
  activeThreadId: string | null;
  onSelectThread: (threadId: string | null) => void;
  /**
   * The CAD instance the app is driving, chosen in the sidebar's card. One selection
   * app-wide — chat, skill execution and skill editing all address it.
   */
  targetSessionId: string | null;
  /** One id per connected vendor (#5) — what the turn sends. `targetSessionId` above is Revit's, for the document context. */
  targetSessions: Partial<Record<HostKind, string>>;
  /**
   * The selected instance's year — whose `active_context.json` the `@` menu
   * reads (item BI). Null when nothing is bound: the menu then has no items.
   */
  revitVersion: string | null;
  /** Reported upward so the instance card can pin the selection while a turn streams. */
  onBusyChange: (busy: boolean) => void;
}) {
  const { t, locale } = useLocale();
  /** The workflow section whose side panel is open (`workflows/`); one at a time. */
  const [openPanel, setOpenPanel] = useState<string | null>(null);
  // The narrow layout, where a section's panel covers the chat rather than sitting beside it.
  const compact = useMediaQuery(SIDEBAR_DRAWER_QUERY);
  const [history, setHistory] = useState<HistoryState>({ kind: "idle" });
  // The chat's Save Skill (item BH5): the artifact that ran becomes a v1 in
  // the year store it ran against — the year is on the artifact itself.
  const saveSkill = useCallback(
    async (artifact: Record<string, unknown>): Promise<string> => {
      if (!token || !orgId) throw new Error(t("chat.signInFirst"));
      // The artifact's own year first; the bound instance's when the artifact
      // says Unknown (an agent from before item BX stamped none).
      // An AutoCAD run's skill goes to the AutoCAD store, into its year's folder
      // (docs/proposals/workspaces.md, stage 1); a Revit run's to its year store.
      const target = artifactTarget(artifact);
      const year =
        target?.kind === "revit"
          ? target.version
          : target === null && revitVersion && /^\d{4}$/.test(revitVersion)
            ? revitVersion
            : null;
      if (!year && target?.kind !== "autocad") throw new Error(t("skills.saveNeedsYear"));
      const str = (key: string): string | undefined => (typeof artifact[key] === "string" ? (artifact[key] as string) : undefined);
      const created = await createSkillsClient({ token, orgId }).createSkill({
        ...(target?.kind === "autocad" ? { vendor: "autocad" as const, version: target.version } : { revitVersion: year ?? "" }),
        name: str("name") ?? "skill",
        displayName: str("displayName"),
        description: str("description"),
        code: str("code") ?? "",
        parameterSchema: Array.isArray(artifact["parameterSchema"]) ? (artifact["parameterSchema"] as never[]) : undefined,
        autoTransaction: artifact["autoTransaction"] as "auto" | boolean | string | undefined,
        language: str("language"),
      });
      return created.displayName;
    },
    [token, orgId, t, revitVersion],
  );
  const [draft, setDraft] = useState("");
  const editorRef = useRef<MentionEditorHandle>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // The transcript mounts only once a conversation has messages (a new one shows the
  // greeting instead), so the follow observer below waits for the element itself.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const attachScroll = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollEl(el);
  }, []);

  // The `@` menu's data (item BI): keyed on the selected instance's year. The
  // suggestion plugin reads its config through a ref, assigned every render,
  // so its closures never see a stale cache.
  // No project, no document context at all (owner, 2026-08-25): the `@` menu,
  // the drop target and the Revit Database read nothing — the same gate as the
  // composer, because a conversation needs a project before it needs elements.
  const docContext = useDocumentContext({ revitVersion: projectId ? revitVersion : null, token, orgId, targetSessionId });
  const suggestionConfig = useRef<DocumentContextSuggestionConfig>(null as unknown as DocumentContextSuggestionConfig);
  suggestionConfig.current = {
    revitVersion: docContext.revitVersion,
    canBrowseDocumentContext: docContext.canBrowseDocumentContext,
    loadingKeys: docContext.loadingKeys,
    keys: docContext.keys,
    displayNames: docContext.displayNames,
    typeItems: docContext.typeItems,
    searchResults: docContext.searchResults,
    fetchKeys: docContext.fetchKeys,
    fetchTypeItems: docContext.fetchTypeItems,
    searchItems: docContext.searchItems,
    refresh: docContext.refresh,
    skillItems: [],
  };
  // Read off `docContext` inside the memo rather than binding here: the pane
  // already binds `fetchTypeItems` further down, and a second `const` for it
  // above its own use would sit in the temporal dead zone when this memo's
  // dependency array is evaluated.
  const docContextValue = useMemo(
    () => ({
      extractable: docContext.extractable,
      enrichment: docContext.enrichment,
      /**
       * The parameter picker's lazy fetch. Cache first — a chip for a type the
       * user has already browsed answers without a request — and only then the
       * type's items, because a mention can be pasted in or restored from
       * history for a type this session never opened.
       */
      item: async (type: string, id: string) => {
        const cached = docContext.lookup(type, id);
        if (cached) return cached;
        const items = await docContext.fetchTypeItems(type);
        return items.find((candidate) => candidate.id === id) ?? null;
      },
    }),
    [docContext.extractable, docContext.enrichment, docContext.lookup, docContext.fetchTypeItems],
  );
  const fileRef = useRef<HTMLInputElement>(null);
  const attachments = useAttachments(token, { localDrawings: true });

  const { models, current: currentModel, select: selectModel, contextWindow } = useModels(
    orgId,
    token,
  );
  // `hitl` is the agent's own default, so starting anywhere else would quietly
  // widen what a turn is allowed to do without asking.
  const [agentMode, setAgentMode] = useState<AgentMode>("hitl");
  // The reasoning chip (#111): one remembered value shared with the skill editor.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort();
  const reasoningApplies = supportsReasoningEffort(currentModel);

  /**
   * The thread the transcript on screen belongs to.
   *
   * The agent mints a thread id on the first turn and sends it back as
   * `thread_info` *mid-stream*. Adopting it moves `activeThreadId` from null to a
   * real id, which looks exactly like the user opening a thread from the sidebar —
   * so the history effect below would fire, fetch the (still empty) transcript
   * and **wipe the answer currently streaming into the screen**.
   *
   * This used to be a set of "threads minted in this session", which skipped the
   * fetch **forever**: mint A, open B, reopen A, and A was skipped while B's
   * messages stayed on screen. Tracking what is *displayed* covers the mid-stream
   * adoption and nothing else — see `shouldLoadHistory`.
   */
  // Starts null, never at `activeThreadId`: on a first mount that restored a
  // thread, seeding it with that id would mark the transcript as already shown and
  // the conversation would open empty and stay empty.
  const shownThreadRef = useRef<string | null>(null);
  const adoptThread = useCallback(
    (threadId: string) => {
      // Before the state change, so the effects below see it on their first run.
      shownThreadRef.current = threadId;
      onSelectThread(threadId);
    },
    [onSelectThread],
  );

  // The workflow sections (`workflows/`). Each one's view is used only while the
  // workflow on screen declares it (`sections` on `GET /workflows`); with no
  // workflow on screen every value below is empty and the pane is the core chat.
  // Which workflows the agent lists, in what state and with which sections.
  const workflowList = useWorkflowList(agent);
  const listedWorkflows = workflowList.kind === "ok" ? workflowList.workflows : [];
  // The workflow on screen, if any (`docs/proposals/workflows.md`). Only then do
  // the sections show, and only then do the turns go to the workflow's own
  // routes: an ordinary chat gets none of their UI and posts to `/ask`.
  const threadWorkflow = useThreadWorkflow(
    token && orgId && projectId && userId ? { token, orgId, projectId, userId, threadId: activeThreadId } : null,
    workflowList,
  );
  const workflow = openWorkflow(workflowId ?? threadWorkflow?.code ?? null, listedWorkflows);
  // Opening a saved conversation whose workflow is not known yet, or entering a
  // workflow before the list has answered: wait, as for the history, rather than
  // draw it as a chat for the moment the read takes — a chat would send to `/ask`,
  // which is the wrong place for a workflow's conversation. Without an agent there
  // is nobody to ask and nothing can be sent either (`blocked`), so the saved
  // transcript is shown as it is.
  const workflowPending = workflowId === null ? threadWorkflow === undefined && agent.ok : workflowList.kind === "loading";
  // A conversation the workflow's view just started is that workflow's from now on,
  // also after the user opens another one and comes back.
  useEffect(() => {
    if (workflow && workflowId && activeThreadId && orgId && projectId && userId) {
      rememberThreadWorkflow({ orgId, projectId, userId, threadId: activeThreadId }, workflow.code);
    }
  }, [workflow, workflowId, activeThreadId, orgId, projectId, userId]);

  const turn = useChatTurn({
    projectId,
    organizationId: orgId,
    userId,
    token,
    threadId: activeThreadId,
    model: currentModel,
    agentMode,
    // Sent only when the chip is shown: a model that does not reason gets no
    // effort, so the agent's own handling of it applies unchanged.
    ...(reasoningApplies ? { reasoningEffort } : {}),
    targetSessionId,
    targetSessions,
    // A workflow's conversation posts to the workflow's routes, chat to `/ask`. By
    // what the conversation is, not by whether its workflow is open: one that is
    // not open is blocked below, and a turn that got past that is refused by the
    // workflow's route rather than answered by chat.
    workflowId: workflow?.code ?? threadWorkflow?.code ?? null,
    onThreadId: adoptThread,
    // The conversation list is fetched once per project, so without this the
    // conversation just created is missing from the sidebar until something
    // else refetches it. The reference reloads from the same place, for the
    // same reason.
    onTurnSettled: reloadThreads,
    // And as soon as a new chat's thread exists, so it is in the list while it runs
    // or waits for an approval, not only once it has finished.
    onThreadCreated: (thread) => {
      // The selection moves to this thread right after, mid-stream. Recorded first,
      // so `workflowPending` has its answer and the reply streaming in stays on screen.
      rememberThreadWorkflow(
        { orgId: thread.organizationId, projectId: thread.projectId, userId: thread.userId, threadId: thread.threadId },
        thread.workflowId,
      );
      reloadThreads();
    },
    mentionEnrichment: docContext.enrichment,
    sessions,
    newChat,
    t,
  });
  const resetTranscript = turn.reset;
  const isLiveThread = turn.isLive;

  /**
   * A starter prompt into the composer. A context prompt's mention is looked
   * up again in the live list before it becomes a chip (`findFreshStarterPromptItem`);
   * when it cannot be found — the type is gone, or no instance is bound — the
   * prompt goes in as text, `@label` flattened, so the words still help.
   */
  const fetchTypeItems = docContext.fetchTypeItems;
  const selectStarter = useCallback(
    async (selection: StarterSelection) => {
      const asText = (text: string) =>
        editorRef.current?.setContent({ type: "doc", content: [{ type: "paragraph", content: text ? [{ type: "text", text }] : [] }] });
      if (selection.kind === "text") {
        asText(selection.text);
        return;
      }
      const { prompt } = selection;
      const items = revitVersion ? await fetchTypeItems(prompt.mention.type) : [];
      const item = findFreshStarterPromptItem(prompt, items);
      if (item) editorRef.current?.setContent(starterPromptToEditorContent(prompt, item));
      else asText(renderStarterPrompt(prompt));
    },
    [revitVersion, fetchTypeItems],
  );

  // The sidebar's instance control is pinned while a turn streams: the tool set
  // is chosen when the turn starts, so a switch could not move it.
  const turnBusy = turn.busy;
  useEffect(() => {
    onBusyChange(turnBusy);
    // Leaving the chat mid-turn must not leave the control locked from a pane
    // that is no longer on screen — the card is visible from every route and
    // would have no way back.
    return () => onBusyChange(false);
  }, [turnBusy, onBusyChange]);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Opening a thread loads its transcript. Keyed on the id alone, with the token
  // from a ref, so a rotated token does not refetch what is already on screen.
  useEffect(() => {
    if (!activeThreadId) {
      // A new chat is its own (draft) conversation in the turn store — empty, or a
      // new chat still running that the user came back to. Nothing to clear.
      shownThreadRef.current = null;
      setHistory({ kind: "idle" });
      return;
    }

    // Already on screen — the mid-stream adoption above, or a re-render. Fetching
    // would replace a live answer with an empty list. And a thread that ran a turn
    // in this session is fresher in the store than in its saved history, which has
    // nothing of a turn still running and only the text of one that finished (#193).
    if (!shouldLoadHistory(activeThreadId, shownThreadRef.current) || isLiveThread(activeThreadId)) {
      // `shownThreadRef` is left alone: the draft effect below reads it as "this pane
      // minted the thread mid-stream", and switching to a live thread is not that.
      setHistory({ kind: "loaded" });
      return;
    }

    let cancelled = false;
    setHistory({ kind: "loading" });

    getThreadMessages(tokenRef.current, activeThreadId, { apiBaseUrl: API_BASE_URL() })
      .then((messages) => {
        if (cancelled) return;
        shownThreadRef.current = activeThreadId;
        resetTranscript(historyToMessages(messages));
        setHistory({ kind: "loaded" });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Owner-only, and it answers 404 for someone else's thread as well as a
        // deleted one — so this cannot claim the conversation was deleted.
        setHistory({
          kind: "error",
          message:
            statusOf(error) === 404
              ? t("chat.threadNotFound")
              : error instanceof Error
                ? error.message
                : String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [activeThreadId, t, resetTranscript, isLiveThread]);

  /**
   * The composer belongs to the conversation it was typed into.
   *
   * Switching threads clears the draft **and any staged attachments**. Carrying
   * either across is not a convenience: the next Enter posts half a question — or
   * someone else's spreadsheet — into a transcript it was never meant for, and
   * the send looks entirely deliberate from the outside.
   *
   * **Skipped for a thread this pane minted itself.** The agent hands back the id
   * mid-stream on the first turn of a new conversation, so `activeThreadId`
   * changes while the user may already be typing their next message. That is the
   * same trap the history effect above guards against, and it is guarded here the
   * same way rather than by a second mechanism that could drift from it.
   */
  const clearAttachments = attachments.clear;
  useEffect(() => {
    if (activeThreadId && activeThreadId === shownThreadRef.current) return;
    setDraft("");
    clearAttachments();
  }, [activeThreadId, clearAttachments]);
  // "New" with no thread open changes no id, so the effect above would not fire.
  const firstNewChat = useRef(newChat);
  useEffect(() => {
    if (newChat === firstNewChat.current) return;
    setDraft("");
    clearAttachments();
  }, [newChat, clearAttachments]);

  // Follow the answer as it streams. Only when already near the bottom, so
  // scrolling up to re-read something is not fought by the next token.
  const messageCount = turn.messages.length;
  const lastText = turn.messages[messageCount - 1]?.text.length ?? 0;
  // The approval card lives in the transcript now, and it usually arrives with
  // no new message and no new text behind it — so without this the turn stops
  // to ask a question that is sitting below the fold, and looks hung.
  const awaitingApproval = turn.interrupt !== null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messageCount, lastText, awaitingApproval]);

  // Text length alone missed most of a tool-heavy turn: new step rows, a row
  // changing state, reasoning, and a jump bigger than the 120px window above
  // all grew the transcript without a scroll. Instead follow every change for
  // as long as the reader is at the bottom; scrolling up opts out, coming back
  // down opts back in, and sending a message always returns to the bottom.
  const followRef = useRef(true);
  useEffect(() => {
    const el = scrollEl;
    if (!el) return;
    const onScroll = () => {
      followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    const observer = new MutationObserver(() => {
      if (followRef.current) el.scrollTop = el.scrollHeight;
    });
    el.addEventListener("scroll", onScroll, { passive: true });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [scrollEl]);
  // Counted, not "is the last message the user's": the reply's placeholder is
  // appended in the same update.
  const sentCount = turn.messages.filter((m) => m.role === "user").length;
  useEffect(() => {
    followRef.current = true;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [sentCount]);
  // Back from another pane: while hidden the transcript has no layout, so a turn that
  // kept streaming grew it without a scroll. Catch up if the reader was following.
  useEffect(() => {
    const el = scrollRef.current;
    if (visible && el && followRef.current) el.scrollTop = el.scrollHeight;
  }, [visible]);

  // Agent state is shown once in the shell's connection area. Keep the
  // composer locked without repeating that state in its placeholder or hint.
  const blocked = !agent.ok
    ? null
    : !projectId
      ? t("projects.noneSelected")
      : !orgId || !userId || !token
        ? t("chat.signInFirst")
        : // A saved workflow conversation whose workflow this agent no longer opens, or
          // this build cannot draw: readable, never continued as an ordinary chat.
          workflowId === null && threadWorkflow && !threadWorkflow.open
          ? t("workflows.notOpen")
          : // Another conversation is driving the instance this one would use (#193):
            // it runs one command at a time, so this turn would fail halfway through.
            turn.instanceInUse && !turn.busy
            ? t("chat.instanceInUse", { host: turn.instanceInUse.kind === "autocad" ? "AutoCAD" : "Revit" })
            : null;
  const composerUnavailable = !agent.ok || blocked !== null;

  // What can stand in for text. A DWG cannot: it is a baseline reference for the
  // AutoCAD tools, not something the model reads, so alone it is no question.
  const ready = attachments.staged.filter((a) => a.status === "ready" && a.kind !== "dwg").length;
  const uploading = attachments.staged.some((a) => a.status === "uploading");

  const composerLockedRef = useRef(false);
  const [requestFocus, setRequestFocus] = useState(0);
  // A workflow's requests are drafted in its Results panel: asking waits until that panel can open.
  const [canRequest, setCanRequest] = useState(false);
  const insertRequest = (text: string) => {
    if (workflow) {
      if (!canRequest) return;
      setDraft((current) => current.trim() ? `${current}\n\n${text}` : text);
      setRequestFocus((current) => current + 1);
      setOpenPanel("results");
      return;
    }
    if (composerLockedRef.current) return;
    insertText(text);
  };
  const insertText = (text: string) => {
    if (draft.trim()) editorRef.current?.appendText(text);
    else editorRef.current?.setContent({ type: "doc", content: text.split("\n").map((line) => ({ type: "paragraph", content: line ? [{ type: "text", text: line }] : [] })) });
  };
  const clearComposer = () => {
    setDraft("");
    attachments.clear();
    editorRef.current?.clear();
  };
  // One section panel at a time; the Revit Database lives in the sidebar and stays as it is.
  const openSectionPanel = (id: string) => {
    setOpenPanel((current) => openPanelFor(current, id));
  };
  // A run cut off before it finished can be continued from the agent's checkpoint —
  // on the user's click only, never by itself (`workflows/continue`). The Progress
  // section marks where it stopped and offers the click there; the header does when another tab is open.
  const continuation = useContinueRun({
    scope: { token, orgId, projectId, userId, threadId: activeThreadId },
    workflowCode: workflow?.code ?? null,
    busy: turn.busy,
    continueRun: turn.continueRun,
  });
  const sections = useWorkflowSections(
    WORKFLOW_SECTIONS,
    {
      scope: { token, orgId, projectId, userId, threadId: activeThreadId },
      workflowId: workflow?.code ?? null,
      workflowDescription: workflow ? workflowText(workflow, locale).description : undefined,
      runControls: (
        <ModelSelector
          models={models}
          current={currentModel}
          onSelect={selectModel}
          reasoningEffort={reasoningApplies ? reasoningEffort : null}
          onReasoningEffort={setReasoningEffort}
          disabled={turn.busy}
        />
      ),
      host: { revitVersion, sessionId: targetSessionId },
      documentContext: docContext,
      busy: turn.busy,
      continuation,
      forcedMode: workflow?.agent.mode ?? null,
      blocked: composerUnavailable || uploading,
      draft,
      setDraft,
      requestFocus,
      canRequest,
      compact,
      send: (text, options) => {
        const { attachments: files, ...rest } = options ?? {};
        turn.send(text, files ?? [], rest);
      },
      composerAttachments: () => attachments.sendable(),
      clearComposer,
      insertRequest,
    },
    workflow?.sections ?? [],
    {
      open: openPanel,
      onOpen: openSectionPanel,
      onClose: (id) => setOpenPanel((current) => closePanel(current, id)),
    },
  );
  const {
    composerLocked,
    requiredMode: sectionRequiredMode,
    messageTitle,
    hideSaveSkill: sectionHidesSaveSkill,
    panel: sectionPanel,
    railActions,
    cards: sectionCards,
  } = composeSections(sections, openPanel);
  // The run mode a workflow forces and whether it offers Save Skill are the workflow's
  // own facts, declared by the agent (`GET /workflows`); a section may still add either.
  const requiredMode = workflow?.agent.mode ?? sectionRequiredMode;
  const hideSaveSkill = (workflow ? !workflow.agent.saveSkill : false) || sectionHidesSaveSkill;
  composerLockedRef.current = composerLocked;
  const onSaveSkill = mayCreateSkill && !hideSaveSkill ? saveSkill : null;
  // The workflow's sections are tabs in its view, one open at a time. With none
  // open the view falls back to the first declared section that has something to
  // show (`defaultSection`): the start form for a new conversation, the steps
  // while a run writes them, the report or the steps after.
  const available = (id: WorkflowSectionId) => railActions.some((action) => action.id === id && !action.disabled);
  const fallbackPanel = workflow
    ? defaultSection(workflow.sections, activeThreadId === null ? "new" : turn.busy ? "running" : "saved", available)
    : null;
  useEffect(() => {
    if (openPanel === null && fallbackPanel) setOpenPanel(fallbackPanel);
  }, [openPanel, fallbackPanel]);
  const resultsReady = workflow !== null && workflow.sections.includes("results") && available("results");
  useEffect(() => setCanRequest(resultsReady), [resultsReady]);
  useEffect(() => {
    if (!workflow) setOpenPanel(null);
  }, [workflow]);
  // Before the run there is only the start form, so no tabs.
  const workflowTabs = workflow && activeThreadId !== null ? (
    <nav className="workflow-view-tabs" aria-label={t("workflow.toolsBar")}>
      {railActions.map((action) => (
        <button
          key={action.id}
          type="button"
          className={`workflow-view-tab${action.open ? " is-active" : ""}`}
          onClick={() => openSectionPanel(action.id)}
          disabled={action.disabled === true}
          aria-pressed={action.open}
        >
          {action.icon}
          <span>{action.label}</span>
        </button>
      ))}
    </nav>
  ) : null;
  // A message can be only attachments. It cannot be sent while one is still
  // uploading: its URL is a local blob the agent cannot read.
  const canSend =
    !composerUnavailable && (draft.trim().length > 0 || ready > 0) && !turn.busy && !uploading && !composerLocked;

  function submit() {
    // The editor's own text, not the last rendered `draft`: Enter submits on a
    // timer, and a keystroke still on its way to `draft` would be cut off (#200).
    const text = editorRef.current?.text() ?? draft;
    if (composerUnavailable || turn.busy || uploading || composerLocked || (text.trim().length === 0 && ready === 0)) return;
    const files = attachments.sendable();
    // Cleared *before* the send, and committed now. The first message swaps the
    // centred composer for the docked one, and the docked editor is seeded from
    // `draft` when it mounts — a send that re-renders ahead of the clearing would
    // mount it holding the sent words. Then the editor is emptied directly rather
    // than trusting `value` turning "" to reach it.
    flushSync(() => {
      setDraft("");
      attachments.clear();
    });
    editorRef.current?.clear();
    turn.send(text, files);
  }

  /**
   * The composer, in `/mobile`'s shape: **one bordered card** holding the
   * textarea, with an action row along its bottom edge — the mode and model
   * chips and the round send button live *inside* the card, not in a caption
   * under it. The reference does it this way and it reads better: the controls
   * that shape the next message belong to the box you type it in.
   */
  const composer = (
    <>
      {/* A way in, not a toolbar: gone the moment there is a conversation. */}
      {turn.messages.length === 0 && !turn.busy && hosts.kind === "loaded"
        && hosts.instances.some((instance) => instance.host_kind === "revit" || instance.host_kind === "autocad") ? (
        <StarterPromptPanel
          disabled={composerUnavailable}
          onSelect={(selection) => void selectStarter(selection)}
          token={token}
          orgId={orgId}
          projectId={projectId}
          targetSessionId={targetSessionId}
          // The same model and the same chip rule the next turn will use (#127), so
          // the prompts are written by the model the user picked and billed at the
          // effort they set. Gated exactly as `useChatTurn` gates it above: a model
          // with no reasoning chip sends no effort.
          model={currentModel}
          {...(reasoningApplies ? { reasoningEffort } : {})}
        />
      ) : null}
      {turn.error ? <ComposerError error={turn.error} /> : null}
      <div className="composer">
        {attachments.staged.length > 0 ? (
          <ul className="attach-list">
            {attachments.staged.map((a) => (
              <li key={a.id} className={`attach-chip is-${a.status}`}>
                {a.kind === "image" ? (
                  <img className="attach-thumb" src={a.url} alt="" aria-hidden="true" />
                ) : (
                  <span className="attach-kind">{a.kind === "pdf" ? "PDF" : a.kind === "dwg" ? "DWG" : "XLS"}</span>
                )}
                <span className="attach-name">{a.filename}</span>
                {a.status === "uploading" ? <Spinner size={12} /> : null}
                {a.status === "failed" ? (
                  <span className="attach-failed">{t("common.failed")}</span>
                ) : null}
                <button
                  type="button"
                  className="attach-remove"
                  disabled={composerLocked}
                  aria-label={t("messageInput.removeAttachment")}
                  onClick={() => attachments.remove(a.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {attachments.error ? (
          <p className="attach-error" role="alert">
            {attachments.error}
          </p>
        ) : null}
        {/* TipTap, not a textarea, so a mention is a chip (item BI). `draft`
            holds the serialised markers; Enter sends, Shift+Enter breaks a
            line, and Enter with the `@` menu open picks from the menu. */}
        <MentionEditor
          ref={editorRef}
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          submitBlocked={turn.busy}
          disabled={composerUnavailable || composerLocked}
          placeholder={blocked ?? t("messageInput.placeholder")}
          mentionsEnabled={true}
          dropEnabled={docContext.canBrowseDocumentContext && !composerLocked}
          suggestionConfig={suggestionConfig}
          onPasteFiles={composerUnavailable || turn.busy ? null : attachments.add}
        />
        {/* Attach on the left; everything that shapes *this* message — mode,
            model, how full the window is — gathered on the right beside Send,
            where the eye already goes to dispatch it. */}
        <div className="composer-actions">
          <div className="composer-controls">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files && !composerLocked) attachments.add(e.target.files);
                // Cleared so picking the same file twice still fires a change.
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="composer-attach"
              disabled={composerUnavailable || turn.busy || composerLocked}
              title={t("messageInput.attach")}
              aria-label={t("messageInput.attach")}
              onClick={() => fileRef.current?.click()}
            >
              +
            </button>
          </div>
          <div className="composer-controls is-right">
            {/* First in the row, as `MessageInput.tsx` orders it: meter, mode,
                model, submit. It reads left-to-right as a sentence about the
                message being composed — how full the window is, then how the
                turn will be run, then by what — and it puts the one item that
                can disappear at the end furthest from the button, so the send
                target does not shift when the meter appears after a turn.

                Measured against whatever the picker shows *now*, so switching to
                a model with a bigger window re-scales on the spot rather than at
                the end of the next turn. Never manufactures a reading: with no
                completed turn there is nothing to pair, and the meter hides. */}
            <ContextMeter usage={resolveContextUsage(turn.usage, contextWindow)} />
            <AgentModeSelector value={requiredMode ?? agentMode} onChange={setAgentMode} disabled={turn.busy || requiredMode !== undefined} />
            <ModelSelector
              models={models}
              current={currentModel}
              onSelect={selectModel}
              reasoningEffort={reasoningApplies ? reasoningEffort : null}
              onReasoningEffort={setReasoningEffort}
              disabled={turn.busy}
            />
          {/* The two controls the reference draws with lucide icons, not with
              text. `↑` and `■` are glyphs: their size, weight and baseline come
              from whichever font the OS resolves them in, so the arrow sat high
              in its circle and the square rendered at a different weight on
              every machine. An inline SVG is the same shape everywhere. */}
          {turn.busy ? (
            <button
              type="button"
              className="composer-send is-stop"
              aria-label={t("chat.stop")}
              title={t("chat.stop")}
              onClick={turn.stop}
            >
              {/* lucide `Square` — an outline, as the reference draws it. */}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="18" height="18" x="3" y="3" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="composer-send"
              aria-label={t("chat.send")}
              title={t("chat.send")}
              disabled={!canSend}
              onClick={submit}
            >
              {/* lucide `ArrowUp`. */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m5 12 7-7 7 7" />
                <path d="M12 19V5" />
              </svg>
            </button>
          )}
          </div>
        </div>
      </div>
      {blocked ? <p className="composer-hint">{blocked}</p> : null}
    </>
  );

  const insertIndexItem = (source: DocumentContextMentionSource) => {
    // A workflow view has no chat editor for a mention chip: the item goes into the request draft as text.
    if (workflow) {
      insertRequest(`${source.name} (${source.type} ${source.id})`);
      return;
    }
    if (composerLocked) return;
    editorRef.current?.insertDocumentContextItem(source);
  };

  /**
   * The chat, and — while it is open — the Revit Database panel, portalled into
   * the sidebar's slot.
   *
   * The panel was the right-hand rail's (item BJ). The rail is gone, merged into
   * the one sidebar, but everything the panel needs is still here: the document
   * context it lists, and the composer a clicked row is inserted into. A portal
   * moves where it is drawn without moving any of that. A dragged row needs
   * nothing at all — the payload rides on the native drag, and the composer's
   * drop handler reads it wherever the drag began.
   *
   * Rendered only while open, so the panel's status poll stops with it.
   */
  // A failed run's error, which in a chat would sit on its reply.
  const lastError = turn.messages.at(-1)?.error ?? null;
  const layout = (chat: React.ReactNode) => (
    <>
      {workflow ? (
        // The workflow's own view (stage 1). It has no chat: its start form, then
        // the run's own sections (steps, report, settings) say what a transcript would.
        // Nothing of it exists in an ordinary chat.
        <div className={`workflow-view${compact ? " is-compact" : ""}`} data-pane="threads" data-workflow={workflow.code}>
          <header className="workflow-view-head">
            {onOpenWorkflows ? (
              <button type="button" className="workflow-view-back" onClick={onOpenWorkflows}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                {t("workflows.title")}
              </button>
            ) : null}
            <div className="workflow-view-titlebar">
              <h2 className="workflow-view-title">{workflowText(workflow, locale).title}</h2>
              {workflowAbout(workflow, locale) && <span className="info-tip workflow-view-standard-info" tabIndex={0} role="note" aria-label={workflowAbout(workflow, locale)!}>
                <InfoIcon size={16} />
                <span className="info-tip-bubble" role="tooltip">{workflowAbout(workflow, locale)}</span>
              </span>}
              {turn.busy ? (
                <>
                  <span className="workflow-view-status" role="status" title={t("workflows.running")}>
                    <Spinner size={18} />
                    <span className="visually-hidden">{t("workflows.running")}</span>
                  </span>
                  <button type="button" className="settings-btn secondary workflow-view-stop" onClick={turn.stop}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="5" width="14" height="14" rx="2" /></svg>
                    {t("chat.stop")}
                  </button>
                </>
              ) : null}
            </div>
            {workflowTabs}
          </header>
          {turn.error ? <ComposerError error={turn.error} /> : null}
          {lastError ? <MessageError error={lastError} /> : null}
          {!turn.busy && !turn.interrupt && openPanel !== "progress" ? <ContinueBar state={continuation} /> : null}
          {turn.interrupt ? <InterruptPanel interrupt={turn.interrupt} busy={turn.busy} onRespond={turn.respond} /> : null}
          <div className="workflow-view-section">
            {sectionPanel ?? (turn.busy ? (
                <p className="workflow-view-empty" role="status">
                  <Spinner size={16} /> {t("workflows.starting")}
                </p>
              ) : null)}
          </div>
        </div>
      ) : (
        chat
      )}
      {databaseOpen && databaseSlot
        ? createPortal(<RevitIndexPanel context={docContext} onInsert={insertIndexItem} onClose={onCloseDatabase} />, databaseSlot)
        : null}
    </>
  );

  if (history.kind === "loading" || workflowPending) {
    return layout(
      <div className="chat" data-pane="threads">
        {/* **No composer while a thread is loading**, and this is a correctness
            rule rather than a tidy one. When the transcript arrives it *replaces*
            what is on screen (`resetTranscript` above), so a message sent in the
            meantime is wiped from the transcript while its request is still in
            flight — the user watches their own question disappear and has no way
            to tell whether it was sent. The reference reaches the same end by
            covering the whole surface with an overlay; hiding the one control
            that can start a turn is the same guarantee with less chrome.

            The sidebar's list stays, so a thread opened by mistake can be swapped
            for another without waiting for the first to finish loading. */}
        <div className="chat-loading">
          <LoadingBlock label={t("chat.loading")} />
        </div>
      </div>,
    );
  }

  if (history.kind === "error") {
    return layout(
      <div className="chat" data-pane="threads">
        <div className="chat-scroll">
          <div className="chat-thread">
            <p className="settings-msg err" role="alert">
              {history.message}
            </p>
          </div>
        </div>
        <div className="composer-wrap">{composer}</div>
      </div>,
    );
  }

  if (turn.messages.length === 0) {
    return layout(
      <div className="chat" data-pane="threads">
        {/* A new conversation opens the way Claude's does (owner, 2026-08-25):
            the greeting and the composer together, centred in the column, so on
            a large screen the eye lands on one thing. The starter prompts ride
            inside `composer`. Once a message exists the transcript takes the
            page and the composer drops to the bottom (below). */}
        <div className="chat-hero">
          <div className="chat-hero-inner">
            {visible && !workflow ? (
              <PluginSetupCard hosts={hosts} plugins={plugins} onOpenPlugins={onOpenPlugins}>
                <div className="chat-welcome">
                  <h2>{t("chat.welcomeTitle")}</h2>
                  <p>{t("chat.welcomeSubtitle")}</p>
                </div>
              </PluginSetupCard>
            ) : (
              <div className="chat-welcome">
                <h2>{t("chat.welcomeTitle")}</h2>
                <p>{t("chat.welcomeSubtitle")}</p>
              </div>
            )}
            {visible && !workflow ? <PluginUpdateCards hosts={hosts} plugins={plugins} /> : null}
            {composer}
          </div>
        </div>
      </div>,
    );
  }

  const lastId = turn.messages[turn.messages.length - 1]?.id;
  const firstUserId = turn.messages.find((message) => message.role === "user")?.id;

  return (
    <DocumentContextProvider value={docContextValue}>
    {layout(
    <div className="chat" data-pane="threads">
      <div className="chat-scroll" ref={attachScroll}>
        <div className="chat-thread">
          {turn.messages.map((message) => {
            const streaming = turn.busy && message.id === lastId;
            // The reply paused for approval hosts its card (item BS).
            const hostsInterrupt = turn.interrupt !== null && message.role === "assistant" && message.id === lastId;
            // A reply with nothing in it yet takes no room: the loading bubble
            // below the thread says the turn is in progress, as the old web's
            // `LoadingIndicator` did, and an empty bubble above it was a gap.
            // `message.error` counts as content: a turn that failed before
            // producing anything has nothing else to show, and skipping it would
            // hide the only report of the failure.
            if (
              message.role === "assistant" &&
              !message.text &&
              !message.steps?.length &&
              !message.reasoning &&
              !message.error &&
              !hostsInterrupt
            )
              return null;
            // A system notice is neither the user's words nor the model's, so it
            // gets no control — the reference renders only the two roles.
            const copyable = message.role !== "system" && message.text.length > 0;
            return (
            <div key={message.id} className={`chat-msg ${message.role}`}>
              {/* `Message.tsx` gates the two roles differently, and the
                  difference is not cosmetic. The user's message is finished the
                  moment it is sent, so its control stays put through the reply
                  that follows; gating both on the turn took the user's own copy
                  button away for exactly as long as the answer took to write.
                  It sits *outside* the bubble, to its left and level with its
                  last line — inside, it reserved a row in every message the user
                  ever sent for a control almost nobody clicks. */}
              {copyable && message.role === "user" ? (
                <div className="msg-actions">
                  <CopyButton text={message.text} />
                </div>
              ) : null}
              <div className="chat-msg-body">
                {/* Reasoning first, then the calls it led to — the order the
                    events arrive in, and the order the reference renders them.
                    The model thinks, then acts; showing the acting above the
                    thinking made every turn read backwards. */}
                {message.reasoning ? (
                  <ReasoningSection text={message.reasoning} complete={!streaming} />
                ) : null}
                {message.steps?.length ? <AgentSteps steps={message.steps} onSaveSkill={onSaveSkill} /> : null}
                {/* Above the text, as the composer stacked them before sending —
                    so what was attached reads as part of the ask, not as an
                    afterthought appended to the answer. */}
                <MessageAttachments attachments={message.attachments} />
                {/* A CSV the reply linked to, drawn as a table — above the text,
                    where the reference puts it, because the answer usually reads
                    "here is the schedule" and the schedule should not be below
                    the sentence pointing at it.

                    Assistant replies only: a link the *user* typed is a link they
                    already have, and fetching it on their behalf is neither asked
                    for nor safe to assume. Streaming replies are skipped too — a
                    half-written URL would fetch a URL that does not exist yet. */}
                {message.role === "assistant" && !streaming
                  ? detectCsvLinks(message.text).map((url) => <CsvPreview key={url} url={url} />)
                  : null}
                {message.text ? (
                  message.role === "user" ? (
                    <UserMessageText text={message.text} title={message.id === firstUserId ? messageTitle : undefined} />
                  ) : (
                    <Markdown text={message.text} mentions={docContext.extractable} />
                  )
                ) : null}
                {/* After everything the reply managed to produce, which is where
                    the reference's `error` event falls in the same sequence: the
                    partial answer and the tool rows above it are the context the
                    failure needs, and putting it first would make every reply
                    read as broken before the reader saw what it did. */}
                <MessageError error={message.error} />
                {/* The old web's Save Skill card (item BR): under a finished reply
                    that delivered code, and not while an approval is pending —
                    `Message.tsx`'s `isContentComplete && !hasPendingInterrupt`.
                    A workflow section can hide it for its conversations. */}
                {!streaming && !turn.interrupt && !hideSaveSkill ? (
                  <SaveSkillCard steps={message.steps} onSaveSkill={onSaveSkill} />
                ) : null}
                {/* In the flow, after everything the reply has said so far —
                    where `Message.tsx` puts it, so the tool row that explains
                    what is being approved is the line above it, not a bubble
                    away. */}
                {hostsInterrupt && turn.interrupt ? (
                  <InterruptPanel
                    interrupt={turn.interrupt}
                    busy={turn.busy}
                    onRespond={turn.respond}
                  />
                ) : null}
                {/* The assistant's sits below its reply and is **not** hidden
                    until hover — the reference leaves it there, and the answer
                    is the thing people actually copy. Still nothing to copy from
                    a reply being written, and the button would keep moving out
                    from under the cursor as the text grows. */}
                {copyable && message.role === "assistant" && !streaming ? (
                  <div className="msg-actions">
                    <CopyButton text={message.text} />
                  </div>
                ) : null}
              </div>
            </div>
            );
          })}

          {!turn.busy && !turn.interrupt ? (
            <CadChangeLinks
              review={cadReviewFromMessages(turn.messages)}
              sessionId={targetSessions.autocad ?? null}
            />
          ) : null}

          {/* What a workflow section shows after the transcript (an end report, say). */}
          {sectionCards.map(({ id, card }) => <Fragment key={id}>{card}</Fragment>)}

          {/* The old web's `LoadingIndicator`: a bubble of bouncing dots after
              the transcript for as long as the turn is running and not waiting
              on the user — whether or not a reply is already streaming above.
              Item BS: it used to show only for a reply with nothing in it yet,
              so a turn mid-tool-call, with rows on screen, looked finished. */}
          {turn.busy && !turn.interrupt ? (
            <div className="chat-msg assistant chat-thinking-row">
              <div className="chat-thinking" role="status" aria-label={t("chat.thinking")}>
                <span className="chat-typing">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            </div>
          ) : null}
          {/* Only if no reply is there to host it — a resumed turn's card always
              has one; this is the belt to that brace. */}
          {turn.interrupt && turn.messages[turn.messages.length - 1]?.role !== "assistant" ? (
            <InterruptPanel
              interrupt={turn.interrupt}
              busy={turn.busy}
              onRespond={turn.respond}
            />
          ) : null}
          {/* The approved plan's floating button, as the web keeps it: last in the
              scrolling transcript so it sticks to the bottom edge, above the composer. */}
          {turn.activePlan ? (
            <ActivePlanPanel plan={turn.activePlan} onDismiss={turn.clearPlan} />
          ) : null}
        </div>
      </div>
      <div className="composer-wrap">{composer}</div>
    </div>,
    )}
    </DocumentContextProvider>
  );
}
